import { ApiSchema } from "@api-types";
import { captureException } from "@sentry/react";
import { ErrorView } from "components/ErrorView/ErrorView";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { hasPolicy$ } from "helpers/hasPolicy";
import React, { useEffect, useState } from "react";
import { ScrollRestoration, useLocation } from "react-router-dom";
import { useSubscription } from "rsm/useSubscription";
import { of } from "rxjs";
import { getFFService } from "services/ff.service";
import { getRoutingService } from "services/routing.service";
import { Globals } from "types/globals";
import { churnBlocked$ } from "hooks/useIsChurned";
import { ChurnBlockedView } from "components/ChurnBlockedView/ChurnBlockedView";

interface ModuleRouteProps {
  El: React.LazyExoticComponent<React.FC<{}>>;
  moduleName: Globals.ModuleNames;
  inPolicy?: ApiSchema.Policy;
  ff?: ApiSchema.FF;
  /** When true, this module is blocked for restricted (churned) non-admin customers. */
  blockWhenChurned?: boolean;
}

const ModuleRoute: React.FC<ModuleRouteProps> = ({ El, moduleName, inPolicy, ff, blockWhenChurned }) => {
  const location = useLocation();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(() => (inPolicy ? null : true));
  const [isChurnBlocked, setIsChurnBlocked] = useState<boolean | null>(() =>
    blockWhenChurned ? null : false,
  );

  useEffect(
    () => getRoutingService().updateModuleNavigation(moduleName, `${location.pathname}${location.search}`),
    [moduleName, location],
  );

  useSubscription(
    inPolicy ? hasPolicy$(inPolicy) : of(true),
    (hasPolicy) => {
      setIsAllowed(inPolicy && !hasPolicy ? false : true);
    },
    [inPolicy],
  );

  useSubscription(blockWhenChurned ? churnBlocked$ : of(false), (blocked) => setIsChurnBlocked(blocked), [
    blockWhenChurned,
  ]);

  useSubscription(
    ff ? getFFService().featureEnabled$(ff) : of(true),
    (hasFeature) => {
      if (!hasFeature) {
        window.location.href = "/";
      }
    },
    [ff],
  );

  useEffect(() => {
    if (isAllowed !== false) return;
    captureException(new Error("ModuleRoute is not allowed"), {
      tags: {
        inPolicy: inPolicy ?? "n/a",
        path: window.location.pathname,
      },
    });
  }, [isAllowed, inPolicy]);

  if (isChurnBlocked === true) {
    return <ChurnBlockedView />;
  }

  if (isAllowed === true && isChurnBlocked === false) {
    return (
      <React.Suspense fallback={<LoadingPage />}>
        <El />
        <ScrollRestoration
          getKey={(location) => {
            // Only use the pathname to identify the scroll position, ignoring query params
            return location.pathname;
          }}
        />
      </React.Suspense>
    );
  } else if (isAllowed === false) {
    return (
      <ErrorView
        errorCode={403}
        title="Permission violation"
        summary="You are not authorized to access this page."
        showHomeButton={true}
      />
    );
  }
  return <LoadingPage />;
};

export default ModuleRoute;
